/**
 * Fertige PDF-Dateien werden getrennt vom laufenden Protokoll in IndexedDB
 * gespeichert. Blobs gehören nicht in localStorage; IndexedDB hält auch
 * größere Fotoprotokolle offline aus, ohne sie in Base64 aufzublähen.
 */
import type { SpracheCode } from './uebersetzungen';

const DATENBANK = 'uebergabe-dokumente';
const VERSION = 1;
const SPEICHER = 'pdfs';

export interface GespeichertePdf {
	schluessel: string;
	protokollId: string;
	sprache: SpracheCode;
	quellHash: string;
	dateiname: string;
	erstelltAm: string;
	blob: Blob;
}

/** Bei Änderungen am PDF-Layout erhöhen, damit alte Dateien neu entstehen. */
const PDF_FORMAT_VERSION = 3;

export function pdfQuellHash(protokoll: unknown): string {
	const text = `${PDF_FORMAT_VERSION}:${JSON.stringify(protokoll)}`;
	let hash = 2166136261;
	for (let index = 0; index < text.length; index += 1) {
		hash ^= text.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return (hash >>> 0).toString(36);
}

function schluesselFuer(protokollId: string, sprache: SpracheCode): string {
	return `${protokollId}:${sprache}`;
}

function oeffneDatenbank(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const anfrage = indexedDB.open(DATENBANK, VERSION);
		anfrage.onupgradeneeded = () => {
			const db = anfrage.result;
			if (!db.objectStoreNames.contains(SPEICHER)) {
				db.createObjectStore(SPEICHER, { keyPath: 'schluessel' });
			}
		};
		anfrage.onsuccess = () => resolve(anfrage.result);
		anfrage.onerror = () => reject(anfrage.error ?? new Error('PDF-Speicher konnte nicht geöffnet werden.'));
	});
}

export async function ladePdfLokal(protokollId: string, sprache: SpracheCode): Promise<GespeichertePdf | null> {
	const db = await oeffneDatenbank();
	try {
		return await new Promise((resolve, reject) => {
			const anfrage = db.transaction(SPEICHER, 'readonly').objectStore(SPEICHER).get(schluesselFuer(protokollId, sprache));
			anfrage.onsuccess = () => resolve((anfrage.result as GespeichertePdf | undefined) ?? null);
			anfrage.onerror = () => reject(anfrage.error ?? new Error('Gespeicherte PDF konnte nicht gelesen werden.'));
		});
	} finally {
		db.close();
	}
}

export async function speicherePdfLokal(
	protokollId: string,
	sprache: SpracheCode,
	quellHash: string,
	dateiname: string,
	blob: Blob
): Promise<GespeichertePdf> {
	const eintrag: GespeichertePdf = {
		schluessel: schluesselFuer(protokollId, sprache),
		protokollId,
		sprache,
		quellHash,
		dateiname,
		erstelltAm: new Date().toISOString(),
		blob
	};
	const db = await oeffneDatenbank();
	try {
		await new Promise<void>((resolve, reject) => {
			const anfrage = db.transaction(SPEICHER, 'readwrite').objectStore(SPEICHER).put(eintrag);
			anfrage.onsuccess = () => resolve();
			anfrage.onerror = () => reject(anfrage.error ?? new Error('PDF konnte nicht lokal gespeichert werden.'));
		});
		return eintrag;
	} finally {
		db.close();
	}
}
